import { Badge, Group, Stack, Text } from '@mantine/core';
import { IconHistory } from '@tabler/icons-react';
import Card from 'components/Card';
import DataTables, { DataTableColumn } from 'components/DataTables';
import Dates from 'components/Dates';
import React, { useMemo } from 'react';
import { CheckInOutHistory, VersionData } from 'types/lcms/courses';
import VersionHistory from './VersionHistory';

interface CheckInCheckOutHistorySectionProps {
  data: CheckInOutHistory[];
  versions: VersionData[];
}

const getLatestTimestamp = (item: CheckInOutHistory): string => {
  const checkIn = item.checkInDateTime || '';
  const checkOut = item.checkOutDateTime || '';
  if (!checkIn && !checkOut) return '';
  return Math.max(
    Number.parseInt(checkIn.replaceAll(/\D/g, '') || '0'),
    Number.parseInt(checkOut.replaceAll(/\D/g, '') || '0')
  ).toString();
};

const CheckInCheckOutHistorySection = ({ data, versions }: CheckInCheckOutHistorySectionProps) => {
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const timestampA = getLatestTimestamp(a);
      const timestampB = getLatestTimestamp(b);
      return timestampB.localeCompare(timestampA);
    });
  }, [data]);

  const columns: DataTableColumn[] = [
    {
      heading: 'Binusian ID',
      props: 'binusianID',
      id: 'binusianID',
      sortable: true,
      active: true
    },
    {
      heading: 'Name',
      props: 'name',
      id: 'name',
      sortable: true,
      active: true
    },
    {
      heading: 'Check In Date Time',
      props: ({ checkInDateTime }: { checkInDateTime: string }) => (
        <Dates date={checkInDateTime} dateFormat='dateNameTime' />
      ),
      id: 'checkInDateTime',
      active: true
    },
    {
      heading: 'Check Out Date Time',
      props: ({ checkOutDateTime }: { checkOutDateTime: string }) => (
        <Dates date={checkOutDateTime} dateFormat='dateNameTime' />
      ),
      id: 'checkOutDateTime',
      active: true
    },
    {
      heading: 'Status',
      props: ({ status }: { status: 'CheckedIn' | 'CheckedOut' }) => (
        <Badge variant='light' color={status === 'CheckedIn' ? 'green' : 'red'} tt={'capitalize'}>
          {status === 'CheckedIn' ? 'Start Revise' : 'End Revise'}
        </Badge>
      ),
      id: 'status',
      sortable: true,
      active: true
    }
  ];

  return (
    <Card>
      <Stack gap={16}>
        <Group gap={8}>
          <IconHistory size={16} />
          <Text c={'dark'} fw={700}>
            Start/End Revise Beelajar LMS History
          </Text>
        </Group>

        <DataTables
          columns={columns}
          data={sortedData}
          loading={false}
          emptyMessage='There is no data history available'
        />
        <VersionHistory data={versions || []} />
      </Stack>
    </Card>
  );
};

export default CheckInCheckOutHistorySection;
